import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { Subscription, combineLatest } from 'rxjs'
import { finalize, first } from 'rxjs/operators'
import { AccountDto } from 'src/app/services/dtos'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { UiService } from 'src/app/services/ui.service'
import { ApiService } from 'src/app/services/api.service'
import { BaseError } from '../../../../utils/errors'
import { AccessLevelService, AccountPermission } from 'src/app/services/access-level.service'

@Component({
  selector: 'app-account-general',
  templateUrl: './account-general.component.html',
  styleUrls: ['./account-general.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountGeneralComponent implements OnInit, OnDestroy {
  accountNameModel = this.formBuilder.group({
    name: ['', [Validators.required]],
  })

  accountDto: AccountDto = null
  private saveAccountSubscription: Subscription = null
  private accountNameUpdated = false
  private accountDtoSubscription: Subscription = null
  private reportRecordCount: number = 0
  isLoading = true
  isLoadingReport = false
  permissionsLoaded = false

  // Permission states
  private hasUpdateAccount = false
  private hasReadBillableResourceReport = false
  private hasReadAccount = false
  private permissionsSubscription: Subscription = null

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private uiService: UiService,
    private apiService: ApiService,
    public accountService: AccountService,
    private accessLevelService: AccessLevelService,
    private router: Router
  ) {}

  ngOnInit() {
    this.initializePermissions()
  }

  private initializePermissions() {
    this.permissionsSubscription = combineLatest([
      this.accessLevelService.hasAccountPermission(AccountPermission.UPDATE_ACCOUNT),
      this.accessLevelService.hasAccountPermission(AccountPermission.READ_BILLABLE_RESOURCE_REPORT),
      this.accessLevelService.hasAccountPermission(AccountPermission.READ_ACCOUNT),
    ])
      .pipe(first())
      .subscribe(([hasUpdateAccount, hasReadBillableResourceReport, hasReadAccount]) => {
        this.hasUpdateAccount = hasUpdateAccount
        this.hasReadBillableResourceReport = hasReadBillableResourceReport
        this.hasReadAccount = hasReadAccount
        this.permissionsLoaded = true

        this.loadAccountData()
      })
  }

  private loadAccountData() {
    this.accountDtoSubscription = this.accountService.accountDtoStream.subscribe(
      (accountDto) => {
        this.accountDto = accountDto
        this.accountNameModel.setValue({ name: accountDto.name })
        this.isLoading = false
      },
      (error) => {
        this.isLoading = false
        if (this.rbacEnabled) {
          this.uiService.announceError(
            new BaseError(
              'Insufficient permissions',
              'You need either Account Admin or Account Read Only role to access account settings.'
            )
          )
          this.router.navigate(['/', this.accountService.accountId])
        } else {
          console.error('Failed to load account data:', error)
        }
      }
    )

    if (this.canViewLicenseCount && !this.usageBillingAvailable) {
      this.isLoadingReport = true
      this.apiService.getBillableResourceReport(this.accountService.accountId).subscribe(
        (report) => {
          this.reportRecordCount = report.length
          this.isLoadingReport = false
        },
        (error) => {
          this.isLoadingReport = false
          this.uiService.announceApiError(error, 'Failed to load billable resource report')
        }
      )
    }
  }

  ngOnDestroy() {
    if (this.updatingAccountName) {
      this.saveAccountSubscription.unsubscribe()
      this.saveAccountSubscription = null
    }

    if (subscriptionActive(this.accountDtoSubscription)) {
      this.accountDtoSubscription.unsubscribe()
      this.accountDtoSubscription = null
    }

    if (subscriptionActive(this.permissionsSubscription)) {
      this.permissionsSubscription.unsubscribe()
      this.permissionsSubscription = null
    }
  }

  updateAccountName() {
    this.saveAccountSubscription = this.accountService
      .updateAccountName$(this.accountNameModel.value.name)
      .pipe(finalize(() => (this.saveAccountSubscription = null)))
      .subscribe(() => (this.accountNameUpdated = true))
  }

  get accountNameDirty() {
    return this.accountDto && this.accountDto.name !== this.accountNameModel.value.name
  }

  get saveNameDisabled() {
    return !this.accountNameDirty || this.updatingAccountName
  }

  get updatingAccountName() {
    return subscriptionActive(this.saveAccountSubscription)
  }

  get updatedAccountName() {
    return this.accountNameUpdated && !this.accountNameDirty
  }

  billableResourceReportUrlCsv(): string {
    return this.apiService.downloadBillableResourceReportCsv(this.accountService.accountId)
  }

  get isOverLicensedBillableResourceCount(): boolean {
    return (
      this.accountService.entitledBillableResourceCount >= 0 &&
      this.accountService.currentBillableResourceCount > this.accountService.entitledBillableResourceCount
    )
  }

  get licencedBillableResourceCount(): string {
    if (this.accountService.entitledBillableResourceCount == -1) {
      return 'unlimited'
    }
    return `${this.accountService.entitledBillableResourceCount}`
  }

  get currentBillableResourceCount(): string {
    return `${this.accountService.currentBillableResourceCount}`
  }

  get canDisplayEntitlementContent(): boolean {
    return this.accountService.entitlementLoaded
  }

  /**
   * Entitlement details live on Usage & Billing, and are repeated here only for accounts
   * that can't reach it — that page is behind a phased-rollout capability and restricted to
   * Flex billing, so Fixed and trial accounts would otherwise lose their subscription
   * expiration outright. Mirrors `usageBillingVisible` in account-navigation.
   */
  get usageBillingAvailable(): boolean {
    return (
      this.accountService.usageReportsEnabled &&
      this.accountService.flexBillingEnabled &&
      this.hasReadBillableResourceReport
    )
  }

  get canEditAccountName() {
    return this.hasUpdateAccount
  }

  get canViewLicenseCount() {
    return this.hasReadBillableResourceReport
  }

  get canViewAccountSettings() {
    return this.hasReadAccount
  }

  get isContentLoading() {
    return !this.permissionsLoaded || this.isLoading
  }

  get reportRecordCountValue() {
    return this.reportRecordCount
  }
}
